import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { User, Mail, Shield, Eye, Briefcase, Building2, Clock } from 'lucide-react';

const ROLE_COLORS = {
  superadmin: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  manager:    { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  viewer:     { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
};
const ROLE_ICONS = { superadmin: Shield, manager: Briefcase, viewer: Eye };

const RoleBadge = ({ role }) => {
  const colors = ROLE_COLORS[role] || ROLE_COLORS.viewer;
  const Icon = ROLE_ICONS[role] || Eye;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`,
      borderRadius: '12px', padding: '3px 12px', fontSize: '13px', fontWeight: 600,
    }}>
      <Icon size={13} /> {role}
    </span>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
      {label}
    </label>
    {children}
  </div>
);

const ReadValue = ({ children }) => (
  <div style={{ fontSize: '15px', color: '#111', padding: '8px 0' }}>{children}</div>
);

const UserProfile = ({ user, onUserUpdated }) => {
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put('/api/auth/me', { full_name: fullName });
      onUserUpdated(res.data);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  return (
    <div style={{ padding: '24px', maxWidth: '640px' }}>
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#111' }}>My Profile</h2>
        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>View and update your account details</p>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '28px' }}>
        {/* Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: '#1e3a5f', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <User size={28} color="white" />
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#111' }}>
              {user?.full_name || user?.email}
            </div>
            <div style={{ marginTop: '4px' }}><RoleBadge role={user?.role} /></div>
          </div>
        </div>

        {/* Editable fields */}
        <form onSubmit={handleSave}>
          <Field label="Full Name">
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="Your full name"
              style={{
                width: '100%', padding: '9px 12px', borderRadius: '7px',
                border: '1px solid #d1d5db', fontSize: '15px', boxSizing: 'border-box',
              }}
            />
          </Field>

          <button
            type="submit"
            disabled={saving}
            style={{
              background: '#1e3a5f', color: 'white', border: 'none',
              borderRadius: '8px', padding: '10px 24px', fontWeight: 600,
              fontSize: '14px', cursor: 'pointer', marginBottom: '28px',
            }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </form>

        {/* Read-only fields */}
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '24px' }}>
          <Field label={<span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Mail size={12} /> Email</span>}>
            <ReadValue>{user?.email}</ReadValue>
          </Field>

          <Field label={<span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Shield size={12} /> Role</span>}>
            <div style={{ padding: '8px 0' }}><RoleBadge role={user?.role} /></div>
          </Field>

          {user?.assigned_tenants?.length > 0 && (
            <Field label={<span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Building2 size={12} /> Assigned Tenants</span>}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '4px 0' }}>
                {user.assigned_tenants.map(t => (
                  <span key={t.id} style={{
                    background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd',
                    borderRadius: '12px', padding: '2px 10px', fontSize: '13px', fontWeight: 600,
                  }}>{t.name}</span>
                ))}
              </div>
            </Field>
          )}

          <Field label={<span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={12} /> Last Login</span>}>
            <ReadValue>{formatDate(user?.last_login_at)}</ReadValue>
          </Field>

          <Field label={<span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={12} /> Member Since</span>}>
            <ReadValue>{formatDate(user?.invite_accepted_at)}</ReadValue>
          </Field>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
